import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function mediaDisponible(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

/**
 * Suit une requete media (`(min-width: 1024px)`, preference de mouvement
 * reduit) et se met a jour quand elle change. Faux sans `matchMedia`.
 */
export function useRequeteMedia(requete: string): boolean {
  const abonner = useCallback(
    (surChangement: () => void) => {
      if (!mediaDisponible()) return () => undefined;
      const liste = window.matchMedia(requete);
      liste.addEventListener('change', surChangement);
      return () => liste.removeEventListener('change', surChangement);
    },
    [requete],
  );
  return useSyncExternalStore(
    abonner,
    () => mediaDisponible() && window.matchMedia(requete).matches,
    () => false,
  );
}

/** Valeur retardee : evite de declencher une requete a chaque frappe. */
export function useValeurDifferee<T>(valeur: T, delai = 350): T {
  const [differee, setDifferee] = useState(valeur);
  useEffect(() => {
    const minuteur = window.setTimeout(() => setDifferee(valeur), delai);
    return () => window.clearTimeout(minuteur);
  }, [valeur, delai]);
  return differee;
}

export type EtatEnregistrement = 'repos' | 'en-attente' | 'enregistrement' | 'enregistre' | 'echec';

/**
 * Enregistrement differe d'un champ libre (zone de notes de la fiche lead).
 * L'appel est declenche apres `delai` ms sans frappe ; `forcer()` permet de
 * l'anticiper (bouton « Enregistrer », perte de focus).
 *
 * Une seule ecriture en route : une valeur a envoyer pendant un envoi part a
 * son retour. En route ensemble, deux ecritures pourraient s'appliquer, ou
 * repondre, a l'envers : l'avant-derniere valeur resterait en base, ou sa
 * reponse tardive reviendrait a l'ecran. « Enregistre » n'est dit qu'une fois la
 * derniere valeur ecrite ; l'echec d'une valeur depassee s'efface devant elle.
 *
 * Au demontage (autre fiche, autre page), la frappe en attente part aussitot,
 * ou au retour de l'envoi en cours, avec la fonction du dernier rendu de ce
 * champ : la ou elle a ete tapee, jamais ailleurs. Effacer le minuteur la
 * perdrait sans rien dire.
 */
export function useEnregistrementDiffere(
  enregistrer: (valeur: string) => Promise<unknown>,
  {
    delai = 900,
    surEchecApresDemontage,
  }: {
    delai?: number;
    /** Echec connu une fois le champ demonte : son etat n'est plus visible, l'appelant le dit autrement (toast). */
    surEchecApresDemontage?: (cause: unknown) => void;
  } = {},
): {
  etat: EtatEnregistrement;
  signaler: (valeur: string) => void;
  forcer: () => void;
} {
  const [etat, setEtat] = useState<EtatEnregistrement>('repos');
  const refValeur = useRef<string | null>(null);
  const refMinuteur = useRef<number | null>(null);
  /** Ecriture en route ; `relancer` : une valeur attend son retour pour partir. */
  const refEnvoi = useRef({ enCours: false, relancer: false });
  const refMonte = useRef(false);
  const refEnregistrer = useRef(enregistrer);
  refEnregistrer.current = enregistrer;
  const refSurEchec = useRef(surEchecApresDemontage);
  refSurEchec.current = surEchecApresDemontage;

  // Nommee : elle se relance elle-meme au retour de l'ecriture en cours.
  const executer = useCallback(function executer(): void {
    // Minuteur echu ou devance : la valeur en attente part maintenant (ou au retour de l'envoi en cours).
    if (refMinuteur.current !== null) window.clearTimeout(refMinuteur.current);
    refMinuteur.current = null;
    const valeur = refValeur.current;
    if (valeur === null) return;
    const envoi = refEnvoi.current;
    if (envoi.enCours) {
      envoi.relancer = true;
      if (refMonte.current) setEtat('enregistrement');
      return;
    }
    refValeur.current = null;
    envoi.enCours = true;
    if (refMonte.current) setEtat('enregistrement');
    /** Vrai quand une valeur plus recente part aussitot : c'est elle qui dira le resultat. */
    const relancer = (): boolean => {
      envoi.enCours = false;
      if (!envoi.relancer) return false;
      envoi.relancer = false;
      executer();
      return true;
    };
    refEnregistrer.current(valeur).then(
      () => {
        if (relancer()) return;
        // Une frappe arrivee pendant l'envoi reste a enregistrer : « Enregistre » serait faux.
        if (refMonte.current && refValeur.current === null) setEtat('enregistre');
      },
      (cause: unknown) => {
        if (relancer()) return;
        if (refMonte.current) setEtat('echec');
        else refSurEchec.current?.(cause);
      },
    );
  }, []);

  const signaler = useCallback(
    (valeur: string) => {
      refValeur.current = valeur;
      setEtat('en-attente');
      if (refMinuteur.current !== null) window.clearTimeout(refMinuteur.current);
      refMinuteur.current = window.setTimeout(executer, delai);
    },
    [delai, executer],
  );

  useEffect(() => {
    refMonte.current = true;
    return () => {
      refMonte.current = false;
      executer();
    };
  }, [executer]);

  return { etat, signaler, forcer: executer };
}

/** Preference d'interface persistee (choix de colonnes, panneau replie...). */
export function useStockageLocal<T>(cle: string, defaut: T): [T, (valeur: T) => void] {
  const [valeur, setValeur] = useState<T>(() => {
    try {
      const brut = window.localStorage.getItem(cle);
      return brut === null ? defaut : (JSON.parse(brut) as T);
    } catch {
      return defaut;
    }
  });

  const definir = useCallback(
    (suivante: T) => {
      setValeur(suivante);
      try {
        window.localStorage.setItem(cle, JSON.stringify(suivante));
      } catch {
        /* stockage indisponible : la preference reste en memoire */
      }
    },
    [cle],
  );

  return [valeur, definir];
}

/**
 * Garde d'une saisie non enregistree. Fermer ou recharger l'onglet passe par
 * la confirmation du navigateur ; suivre un lien vers une autre page ouvre
 * `destination`, que l'ecran confirme a sa facon. Le routeur declaratif de
 * l'application ne sait pas bloquer une navigation (`useBlocker` exige un
 * routeur de donnees) : on intercepte donc les clics sur les liens. Le bouton
 * Precedent du navigateur et les navigations programmees ne sont pas couverts.
 */
export function useGardeModifications(actif: boolean): { destination: string | null; quitter: () => void; rester: () => void } {
  const naviguer = useNavigate();
  const { pathname, search } = useLocation();
  const ici = `${pathname}${search}`;
  const [destination, setDestination] = useState<string | null>(null);

  useEffect(() => {
    if (!actif) return undefined;
    const surDepart = (evenement: BeforeUnloadEvent): void => {
      evenement.preventDefault();
      // Certains navigateurs n'affichent leur confirmation qu'avec returnValue.
      evenement.returnValue = '';
    };
    const surClic = (evenement: MouseEvent): void => {
      if (evenement.defaultPrevented || evenement.button !== 0) return;
      if (evenement.metaKey || evenement.ctrlKey || evenement.shiftKey || evenement.altKey) return;
      const lien = evenement.target instanceof Element ? evenement.target.closest('a[href]') : null;
      if (!(lien instanceof HTMLAnchorElement) || lien.hasAttribute('download')) return;
      if (lien.target !== '' && lien.target !== '_self') return;
      // Ancre de la page (lien d'evitement « Aller au contenu principal ») : rien n'est quitte (R12).
      if (lien.getAttribute('href')?.startsWith('#')) return;
      const cible = new URL(lien.href, window.location.href);
      if (cible.origin !== window.location.origin) return;
      // Meme page, a l'ancre pres : rien n'est quitte non plus.
      if (`${cible.pathname}${cible.search}` === ici) return;
      evenement.preventDefault();
      setDestination(`${cible.pathname}${cible.search}${cible.hash}`);
    };
    window.addEventListener('beforeunload', surDepart);
    // Phase de capture : avant le gestionnaire du lien, qui renonce a naviguer
    // quand l'evenement est deja consomme (`defaultPrevented`).
    document.addEventListener('click', surClic, true);
    return () => {
      window.removeEventListener('beforeunload', surDepart);
      document.removeEventListener('click', surClic, true);
    };
  }, [actif, ici]);

  const quitter = (): void => {
    if (destination !== null) naviguer(destination);
    setDestination(null);
  };
  return { destination, quitter, rester: () => setDestination(null) };
}
